// for random constants in the code so they can be found easier

// Feature flag for the new "Arb Offer" feature — lets an offer rest on the
// book even when it crosses the opposing side, instead of matching existing
// liquidity. Set to true once the backend contract is confirmed and shipped.
export const ARB_OFFER_ENABLED = true;

// Affiliate signup codes whose direct referrals cannot use the cashier
// (deposit / redemption). A user is blocked when their own signup `code`
// is one of these. Note this is a flat, direct-signup check — it blocks
// users who signed up directly under one of these codes (e.g. THFC4 with
// code 'thfc'), not players those users later refer further down the tree.
// Compare lowercased since signup codes are stored lowercased.
export const cashierBlockedCodes = [
  'bagel',
  'thfc',
  'nickmma',
  'ranger',
  'mango4c',
  'mongoose',
];

// Specific usernames blocked from the cashier regardless of their signup code.
export const cashierBlockedUsernames = ['nhl001'];

// Single source of truth for "may this user see/use the cashier?" — used by
// both the account-dropdown nav link and the /cashier route guard so they
// can never drift apart. 'demo' stays blocked to preserve prior behavior.
export function userCanUseCashier(user) {
  const username = (user?.username || '').toLowerCase();
  if (cashierBlockedUsernames.includes(username)) return false;
  const code = (user?.code || '').toLowerCase();
  if (code === 'demo') return false;
  return !cashierBlockedCodes.includes(code);
}

// this is used in exchange view
export const validSports = [
  'baseball',
  'fighting',
  'basketball',
  'hockey',
  'football',
];

export const commissionDiscounts = {
  mlb: {
    moneyline: 0.0075,
  },
};

export const sportMap = {
  NCAAF: ['NCAAF'],
  CFL: ['CFL'],
  UFL: ['UFL'],
  'NFL-PRESEASON': ['NFL-PRESEASON'],
  NBA: ['NBA', '1H-NBA', 'WEST', 'EAST'],
  WNBA: ['WNBA'],
  NCAAB: ['NCAAB', '1H-NCAAB'],
  WEST: ['WEST', 'EAST', 'NBA', '1H-NBA'],
  EAST: ['EAST', 'WEST', 'NBA', '1H-NBA'],
  '1H-NBA': ['NBA', '1H-NBA'],
  '1H-NCAAB': ['NCAAB', '1H-NCAAB'],
  NHL: ['NHL'],
  'OLYMPIC-HOCKEY': ['OLYMPIC-HOCKEY'],
  PGA: ['PGA'],
  'MAKE-THE-CUT': ['PGA'],
  'TOP-10': ['PGA'],
  'ROUND-1': ['PGA'],
  UFCMMA: ['UFCMMA'],
  BOXING: ['BOXING'],
  MLB: ['MLB', 'F5-MLB', 'WBC'],
  'F5-MLB': ['MLB', 'F5-MLB', 'WBC'],
  WBC: ['WBC', 'F5-MLB' , 'MLB'],
  //  politics: ['ELECTION', 'ELECTORAL COLLEGE'],
};

// setting options from league header
export const oddsViews = [
  { name: 'Sportsbook', key: 'sportsbook', id: '1' },
  { name: 'Exchange', key: 'exchange', id: '2' },
];

export const is2FATypes = [
  { name: 'Enabled', key: 'enabled', id: '1' },
  { name: 'Disabled', key: 'disabled', id: '2' },
];

export const twoFATypes = [
  { name: 'Email', key: 'email', id: '1' },
  { name: 'Telegram', key: 'telegram', id: '2' },
];

export const oddsTypes = [
  { name: 'American', key: 'american', id: '1' },
  { name: 'Decimal', key: 'decimal', id: '2' },
  { name: 'Probability', key: 'impliedProbability', id: '3' },
];

export const sortBy = [
  { name: 'Start Time', value: 'default', id: '0' },
  { name: 'Rotation Numbers', value: 'rotation_number', id: '1' },
  { name: 'Matched Volume', value: 'matched_volume', id: '2' },
  { name: 'Open Interest', value: 'open_interest', id: '3' },
];

export const displayRotationOptions = [
  { name: 'Yes', key: 'yes', id: '1' },
  { name: 'No', key: 'no', id: '2' },
];

export const viewCommissionOptions = [
  { name: 'Yes', key: 'yes', id: '1' },
  { name: 'No', key: 'no', id: '2' },
];

export const liveSlippageOptions = [
  { name: 'No Slippage', key: 'strict', id: '1' },
  { name: 'Accept Slippage', key: 'slippage', id: '2' },
];

// Ceiling on the live-slippage tolerance, in implied-probability percentage
// points. The feature is 100% frontend (CTO directive) — this constant is
// the ONLY cap; there is no server-side counterpart.
export const liveSlippageMaxPct = 5;

// Reference lines shown in the settings preview table as the user moves the
// slippage slider. Each row's "new max" is computed live via slippageFloorOdds.
// Spread of favorites and underdogs so the effect is visible across the board.
export const liveSlippageExampleOdds = [
  200, 120, -100, -110, -150, -200, -300, -500,
];

export const sidePanelOptions = [
  { name: 'Best Offers', key: 'best_offers', id: '3' },
  { name: 'Unmatched', key: 'unmatched', id: '1' },
  { name: 'Matched', key: 'matched', id: '2' },
  // { name: 'Chat', key: 'chat', id: '4' },
];

export const riskWinOptions = ['100', '250', '500', '1000', '2000', '5000'];

export const createOfferUnitModeOptions = [
  { name: 'Risk a Unit', key: 'riskAUnit', id: '1' },
  { name: 'Win a Unit', key: 'winAUnit', id: '2' },
  { name: 'The Cardano', key: 'theCardano', id: '3' },
];

export const expiryItems = [
  { text: '15 min', value: 15 },
  { text: '3 hours', value: 180 },
  { text: '12 hours', value: 720 },
  { text: '24 hours', value: 1440 },
];

export const leagueToSportMap = {
  MLB: 'baseball',
  WBC: 'baseball',
  WEST: 'basketball',
  EAST: 'basketball',
  UFCMMA: 'fighting',
  BOXING: 'fighting',
  NCAAF: 'football',
  CFL: 'football',
  UFL: 'football',
  NFL: 'football',
  'NFL-PRESEASON': 'football',
  NBA: 'basketball',
  WNBA: 'basketball',
  NCAAB: 'basketball',
  ATP: 'tennis',
  NHL: 'hockey',
  'OLYMPIC-HOCKEY': 'hockey',
};

// URL-slug -> published-league mapping for the streaming-api price feed
// subscription router. Used when the URL token in `/exchange/:sport/:league`
// does NOT match the league string emitted by the socketapi publisher.
//
// Slugs not in this map fall back to uppercased(slug), which works for the
// common cases (mlb -> MLB, nba -> NBA, nfl -> NFL, etc.). Add entries here
// as new mismatches are discovered. Each slug can resolve to multiple real
// leagues if a single page should listen across them.
export const slugToLeagues = {
  ufc: ['UFCMMA'],
};

/**
 * Resolve a `:league` URL slug into one or more published league IDs that the
 * streaming-api will recognize for routing. Always returns an array.
 */
export function resolveLeagueSlug(slug) {
  if (!slug) return [];
  const known = slugToLeagues[String(slug).toLowerCase()];
  if (known) return known;
  return [String(slug).toUpperCase()];
}

export const monkeyModes = [
  { name: 'Defensive', id: '1' },
  { name: 'Offensive', id: '2' },
  { name: 'Custom', id: '3' },
];

export const oddsModes = [
  { name: 'Percentage', id: '1' },
  { name: 'American', id: '2' },
];

export const monkeyVigMap = {
  UFCMMA: 0.018,
  NCAAF: 0.016,
  NFL: 0.016,
  NBA: 0.018,
  ATP: 0.02,
  DEFAULT: 0.014,
};

export const betTypeKeys = [
  'futuresTeam',
  'isFutures',
  'id',
  'league',
  'sport',
  'start',
  'ended',
  'participants',
  'oddsDelta',
  'eventName',
  'parentGameID',
  'mainHomeSpread',
  'mainAwaySpread',
  'mainTotal',
  'cheapDataUID',
  'position',
  'live',
  'matchedVolume',
  'periodName',
  'messageType',
];

export const excelFields = {
  User: 'username',
  '4CASTERS without commission': 'grossWithoutCommission',
  'Commission 1%': 'commissionPaidSumNeat',
  '4CASTERS net balance': 'netTotal',
};

export const monkeySpreadLeagues = ['NFL', 'NCAAF'];

export const alwaysAllowArbTakes = ['j0011', 'evanuser', 'here2stay'];

export const yesNoLeagues = [
  'AWARDS',
  'MLB-SERIES',
  'ATP',
  'WTA',
  'WINNER',
  'WEST',
  'SOUTH',
  'MIDWEST',
  'EAST',
  'MASTERS',
  'NBA-SERIES',
  'ECF',
  'WCF',
  'TITLE',
  'NHL-SERIES',
];

// Soccer "outright" markets that should trade as a simple 2-way market
// (e.g. "Argentina win World Cup" vs "Does NOT win") instead of the default
// 3-way home/draw/away match market. These render and place through the normal
// moneyline path so the two sides form a single cross-matching book.
//
// Matched as a normalized substring of the league code (case-insensitive, with
// spaces/dashes removed), so 'SOCCER-WINNER', 'WORLD-CUP-WINNER',
// 'TO-ADVANCE', and 'WORLD-CUP-TO-ADVANCE' are all covered. Add new 2-way
// soccer league keywords here.
export const TWO_WAY_SOCCER_LEAGUE_KEYWORDS = ['WINNER', 'TOADVANCE'];

//Options used in createOfferModal when props.isNewOffer and type total.
export const ouSideModal = [
  { longName: 'Over', id: '1' },
  { longName: 'Under', id: '2' },
];

export const moneyline1x2SideModal = [
  { longName: 'Yes', id: '1' },
  { longName: 'No', id: '2' },
];

// Users that appear under "angie" affiliate summary for display only (no commission)
export const angieDisplayOnlyUsers = [
  'nocryball',
  'jaso85',
  'jmm',
  'gores',
  'raggy',
  'johnstargaryen',
  'lmatthews08',
  'dhholden1',
  'liam',
  't100',
  'abc6250',
  'payton',
  'wilson1',
  'tiger80',
  'markpattinson',
  'curtains',
  'erickd1487',
  'subtlealterity',
  'cpfeiffer24',
  'butters7',
  'vegas20',
  'tigerw',
  'whitecryptocow',
  'nonstop',
  'magickmilf',
  'ccl4casters',
  'cclsoccer',
  'mollytest',
  'sportingdijon',
  'sportingdijon2',
  'cactus44',
  'j0011',
  'sawyer',
  'poodle',
  'buckdancer',
  'exclusive',
];

// Users who asked for the per-market risk/win badges to stay off the mobile
// game rows. They get the game-level aggregate next to the date instead — the
// pre-June-2026 display. Desktop is unaffected.
export const aggregatePositionOnlyUsers = ['george'];

// Soccer leagues organized by continent and country
export const soccerLeagues = {
  europe: {
    name: 'Europe',
    countries: {
      england: {
        name: 'England',
        leagues: [
          { code: 'ENGLAND-EPL', name: 'England Premier League' },
          { code: 'ENGLAND-CHAMPIONSHIP', name: 'England Championship' },
          { code: 'ENGLAND-LEAGUE-1', name: 'England League 1' },
          { code: 'ENGLAND-LEAGUE-2', name: 'England League 2' },
          { code: 'ENGLAND-FA-CUP', name: 'England FA Cup' },
          { code: 'ENGLAND-EFL-CUP', name: 'England EFL Cup (Carabao Cup)' },
          { code: 'ENGLAND-NATIONAL-LEAGUE', name: 'England National League' },
        ],
      },
      spain: {
        name: 'Spain',
        leagues: [
          { code: 'SPAIN-LA-LIGA', name: 'Spain La Liga' },
          { code: 'SPAIN-LA-LIGA-2', name: 'Spain La Liga 2' },
          { code: 'SPAIN-COPA-DEL-REY', name: 'Spain Copa del Rey' },
          { code: 'SPAIN-SUPERCOPA', name: 'Spain Supercopa de España' },
        ],
      },
      germany: {
        name: 'Germany',
        leagues: [
          { code: 'GERMANY-BUNDESLIGA', name: 'Germany Bundesliga' },
          { code: 'GERMANY-BUNDESLIGA-2', name: 'Germany Bundesliga 2' },
          { code: 'GERMANY-DFB-POKAL', name: 'Germany DFB-Pokal' },
          { code: 'GERMANY-DFL-SUPERCUP', name: 'Germany DFL-Supercup' },
        ],
      },
      italy: {
        name: 'Italy',
        leagues: [
          { code: 'ITALY-SERIE-A', name: 'Italy Serie A' },
          { code: 'ITALY-SERIE-B', name: 'Italy Serie B' },
          { code: 'ITALY-COPPA-ITALIA', name: 'Italy Coppa Italia' },
          { code: 'ITALY-SUPERCOPPA', name: 'Italy Supercoppa Italiana' },
        ],
      },
      france: {
        name: 'France',
        leagues: [
          { code: 'FRANCE-LIGUE-1', name: 'France Ligue 1' },
          { code: 'FRANCE-LIGUE-2', name: 'France Ligue 2' },
          { code: 'FRANCE-COUPE-DE-FRANCE', name: 'France Coupe de France' },
          { code: 'FRANCE-COUPE-DE-LA-LIGUE', name: 'France Coupe de la Ligue' },
        ],
      },
      portugal: {
        name: 'Portugal',
        leagues: [
          { code: 'PORTUGAL-1-LIGA', name: 'Portugal 1 Liga' },
          { code: 'PORTUGAL-2-LIGA', name: 'Portugal 2 Liga' },
          { code: 'PORTUGAL-TACA-DE-PORTUGAL', name: 'Portugal Taça de Portugal' },
        ],
      },
      netherlands: {
        name: 'Netherlands',
        leagues: [
          { code: 'NETHERLANDS-EREDIVISIE', name: 'Netherlands Eredivisie' },
          { code: 'NETHERLANDS-EERSTE-DIVISIE', name: 'Netherlands Eerste Divisie' },
          { code: 'NETHERLANDS-KNVB-CUP', name: 'Netherlands KNVB Cup' },
        ],
      },
      belgium: {
        name: 'Belgium',
        leagues: [
          { code: 'BELGIUM-PRO-LEAGUE', name: 'Belgium Pro League' },
          { code: 'BELGIUM-CHALLENGER-PRO-LEAGUE', name: 'Belgium Challenger Pro League' },
          { code: 'BELGIUM-JUPILER-LEAGUE', name: 'Belgium Jupiler League' },
        ],
      },
      scotland: {
        name: 'Scotland',
        leagues: [
          { code: 'SCOTLAND-PREMIERSHIP', name: 'Scotland Premiership' },
          { code: 'SCOTLAND-CHAMPIONSHIP', name: 'Scotland Championship' },
          { code: 'SCOTLAND-SCOTTISH-CUP', name: 'Scotland Scottish Cup' },
          { code: 'SCOTLAND-PREMIER-LEAGUE', name: 'Scotland Premier League' },
        ],
      },
      ireland: {
        name: 'Ireland',
        leagues: [
          { code: 'IRELAND-EIRCOM-LEAGUE', name: 'Ireland Eircom League' },
        ],
      },
      turkey: {
        name: 'Turkey',
        leagues: [
          { code: 'TURKEY-SUPER-LIG', name: 'Turkey Süper Lig' },
          { code: 'TURKEY-TFF-1-LIG', name: 'Turkey TFF First League' },
        ],
      },
      russia: {
        name: 'Russia',
        leagues: [
          { code: 'RUSSIAN-PREMIER-LEAGUE', name: 'Russian Premier League' },
          { code: 'RUSSIA-FNL', name: 'Russia FNL' },
        ],
      },
      ukraine: {
        name: 'Ukraine',
        leagues: [
          { code: 'UKRAINIAN-PREMIER-LEAGUE', name: 'Ukrainian Premier League' },
        ],
      },
      greece: {
        name: 'Greece',
        leagues: [
          { code: 'GREECE-SUPERLEAGUE', name: 'Greece Superleague' },
        ],
      },
      austria: {
        name: 'Austria',
        leagues: [
          { code: 'AUSTRIA-BUNDESLIGA', name: 'Austria Bundesliga' },
        ],
      },
      switzerland: {
        name: 'Switzerland',
        leagues: [
          { code: 'SWITZERLAND-SUPER-LEAGUE', name: 'Switzerland Super League' },
        ],
      },
      denmark: {
        name: 'Denmark',
        leagues: [
          { code: 'DENMARK-SAS-LIGAEN', name: 'Denmark SAS Ligaen' },
        ],
      },
      sweden: {
        name: 'Sweden',
        leagues: [
          { code: 'SWEDEN-ALLSVENSKAN', name: 'Sweden Allsvenskan' },
        ],
      },
      slovenia: {
        name: 'Slovenia',
        leagues: [
          { code: 'SLOVENIA-PRVA-LIGA', name: 'Slovenia Prva Liga' },
        ],
      },
      norway: {
        name: 'Norway',
        leagues: [
          { code: 'NORWAY-ELITESERIEN', name: 'Norway Eliteserien' },
        ],
      },
      poland: {
        name: 'Poland',
        leagues: [
          { code: 'POLAND-EKSTRAKLASA', name: 'Poland Ekstraklasa' },
        ],
      },
      czechRepublic: {
        name: 'Czech Republic',
        leagues: [
          { code: 'CZECH-REPUBLIC-FIRST-LEAGUE', name: 'Czech Republic First League' },
        ],
      },
      croatia: {
        name: 'Croatia',
        leagues: [
          { code: 'CROATIA-HNL-LEAGUE-1', name: 'Croatia HNL League 1' },
        ],
      },
      serbia: {
        name: 'Serbia',
        leagues: [
          { code: 'SERBIA-SUPERLIGA', name: 'Serbia SuperLiga' },
        ],
      },
      romania: {
        name: 'Romania',
        leagues: [
          { code: 'ROMANIA-LIGA-1', name: 'Romania Liga I' },
        ],
      },
      hungary: {
        name: 'Hungary',
        leagues: [
          { code: 'HUNGARY-NB-1', name: 'Hungary NB 1' },
        ],
      },
      cyprus: {
        name: 'Cyprus',
        leagues: [
          { code: 'CYPRUS-FIRST-DIVISION', name: 'Cyprus First Division' },
        ],
      },
    },
    competitions: [
      { code: 'CHAMPIONS-LEAGUE', name: 'UEFA Champions League' },
      { code: 'EUROPA-LEAGUE', name: 'UEFA Europa League' },
      { code: 'CONFERENCE-LEAGUE', name: 'UEFA Europa Conference League' },
      { code: 'UEFA-SUPER-CUP', name: 'UEFA Super Cup' },
      { code: 'EURO', name: 'UEFA European Championship' },
      { code: 'EURO-QUALIFIERS', name: 'UEFA Euro Qualifiers' },
      { code: 'NATIONS-LEAGUE', name: 'UEFA Nations League' },
    ],
  },
  southAmerica: {
    name: 'South America',
    countries: {
      brazil: {
        name: 'Brazil',
        leagues: [
          { code: 'BRAZIL-SERIE-A', name: 'Brazil Serie A' },
          { code: 'BRAZIL-SERIE-B', name: 'Brazil Serie B' },
          { code: 'BRAZIL-COPA-DO-BRASIL', name: 'Brazil Copa do Brasil' },
          { code: 'BRAZIL-CAMPEONATO-PAULISTA', name: 'Brazil Campeonato Paulista' },
          { code: 'BRAZIL-CAMPEONATO-CARIOCA', name: 'Brazil Campeonato Carioca' },
          { code: 'BRAZIL-CAMPEONATO-MINEIRO', name: 'Brazil Campeonato Mineiro' },
          { code: 'BRAZIL-CAMPEONATO-GAUCHO', name: 'Brazil Campeonato Gaúcho' },
        ],
      },
      argentina: {
        name: 'Argentina',
        leagues: [
          { code: 'ARGENTINA-LIGA-PROFESIONAL', name: 'Argentina Liga Profesional' },
          { code: 'ARGENTINA-PRIMERA-NACIONAL', name: 'Argentina Primera Nacional' },
          { code: 'ARGENTINA-COPA-ARGENTINA', name: 'Argentina Copa Argentina' },
          { code: 'ARGENTINA-PRIMERA-DIVISION', name: 'Argentina Primera División' },
        ],
      },
      colombia: {
        name: 'Colombia',
        leagues: [
          { code: 'COLOMBIA-CATEGORIA-PRIMERA-A', name: 'Colombia Categoría Primera A' },
        ],
      },
      chile: {
        name: 'Chile',
        leagues: [
          { code: 'CHILE-PRIMERA-DIVISION', name: 'Chile Primera División' },
        ],
      },
      uruguay: {
        name: 'Uruguay',
        leagues: [
          { code: 'URUGUAY-PRIMERA-DIVISION', name: 'Uruguay Primera División' },
        ],
      },
      peru: {
        name: 'Peru',
        leagues: [
          { code: 'PERU-LIGA-1', name: 'Peru Liga 1' },
          { code: 'PERU-PRIMERA-DIVISION', name: 'Peru Primera Division' },
        ],
      },
      ecuador: {
        name: 'Ecuador',
        leagues: [
          { code: 'ECUADOR-LIGA-PRO', name: 'Ecuador LigaPro Serie A' },
        ],
      },
      paraguay: {
        name: 'Paraguay',
        leagues: [
          { code: 'PARAGUAY-DIVISION-PROFESIONAL', name: 'Paraguay División Profesional' },
          { code: 'PARAGUAY-1ER-DIVISION', name: 'Paraguay 1er Division' },
        ],
      },
      venezuela: {
        name: 'Venezuela',
        leagues: [
          { code: 'VENEZUELA-PRIMERA-DIVISION', name: 'Venezuela Primera División' },
        ],
      },
      bolivia: {
        name: 'Bolivia',
        leagues: [
          { code: 'BOLIVIA-DIVISION-PROFESIONAL', name: 'Bolivia División Profesional' },
        ],
      },
    },
    competitions: [
      { code: 'COPA-LIBERTADORES', name: 'Copa Libertadores' },
      { code: 'COPA-SUDAMERICANA', name: 'Copa Sudamericana' },
      { code: 'RECOPA-SUDAMERICANA', name: 'Recopa Sudamericana' },
      { code: 'COPA-AMERICA', name: 'Copa América' },
      { code: 'CONMEBOL-QUALIFIERS', name: 'CONMEBOL World Cup Qualifiers' },
    ],
  },
  northAmerica: {
    name: 'North America',
    countries: {
      usa: {
        name: 'United States',
        leagues: [
          { code: 'USA-MLS', name: 'USA Major League Soccer' },
          { code: 'USA-USL-CHAMPIONSHIP', name: 'USA USL Championship' },
          { code: 'USA-USL-LEAGUE-ONE', name: 'USA USL League One' },
          { code: 'USA-NWSL', name: 'USA National Women\'s Soccer League' },
          { code: 'USA-US-OPEN-CUP', name: 'USA U.S. Open Cup' },
        ],
      },
      mexico: {
        name: 'Mexico',
        leagues: [
          { code: 'MEXICO-LIGA-MX', name: 'Mexico Liga MX' },
          { code: 'MEXICO-LIGA-EXPANSION-MX', name: 'Mexico Liga de Expansión MX' },
          { code: 'COPA-MX', name: 'Copa MX' },
          { code: 'MEXICO-CAMPEONES-CUP', name: 'Mexico Campeones Cup' },
        ],
      },
      canada: {
        name: 'Canada',
        leagues: [
          { code: 'CANADA-CPL', name: 'Canada Canadian Premier League' },
        ],
      },
      costaRica: {
        name: 'Costa Rica',
        leagues: [
          { code: 'COSTA-RICA-PRIMERA-DIVISION', name: 'Costa Rica Primera División' },
          { code: 'COSTA-RICA-1ST-DIVISION', name: 'Costa Rica 1st Division' },
        ],
      },
      honduras: {
        name: 'Honduras',
        leagues: [
          { code: 'HONDURAS-LIGA-NACIONAL', name: 'Honduras Liga Nacional' },
        ],
      },
      guatemala: {
        name: 'Guatemala',
        leagues: [
          { code: 'GUATEMALA-LIGA-NACIONAL', name: 'Guatemala Liga Nacional' },
        ],
      },
      elSalvador: {
        name: 'El Salvador',
        leagues: [
          { code: 'EL-SALVADOR-PRIMERA-DIVISION', name: 'El Salvador Primera División' },
        ],
      },
      panama: {
        name: 'Panama',
        leagues: [
          { code: 'PANAMA-LPF', name: 'Panama Liga Panameña de Fútbol' },
        ],
      },
      jamaica: {
        name: 'Jamaica',
        leagues: [
          { code: 'JAMAICA-PREMIER-LEAGUE', name: 'Jamaica Premier League' },
        ],
      },
    },
    competitions: [
      { code: 'CONCACAF-CHAMPIONS-CUP', name: 'CONCACAF Champions Cup' },
      { code: 'CONCACAF-GOLD-CUP', name: 'CONCACAF Gold Cup' },
      { code: 'CONCACAF-NATIONS-LEAGUE', name: 'CONCACAF Nations League' },
      { code: 'LEAGUES-CUP', name: 'Leagues Cup' },
      { code: 'CONCACAF-QUALIFIERS', name: 'CONCACAF World Cup Qualifiers' },
    ],
  },
  asia: {
    name: 'Asia',
    countries: {
      japan: {
        name: 'Japan',
        leagues: [
          { code: 'JAPAN-J-LEAGUE', name: 'Japan J League' },
          { code: 'J2-LEAGUE', name: 'J2 League' },
          { code: 'J3-LEAGUE', name: 'J3 League' },
          { code: 'EMPERORS-CUP', name: 'Emperor\'s Cup' },
        ],
      },
      southKorea: {
        name: 'South Korea',
        leagues: [
          { code: 'SOUTH-KOREA-K-LEAGUE-1', name: 'South Korea K League 1' },
          { code: 'SOUTH-KOREA-K-LEAGUE-2', name: 'South Korea K League 2' },
        ],
      },
      china: {
        name: 'China',
        leagues: [
          { code: 'CHINA-SUPER-LEAGUE', name: 'China Super League' },
          { code: 'CHINA-LEAGUE-ONE', name: 'China League One' },
        ],
      },
      saudiArabia: {
        name: 'Saudi Arabia',
        leagues: [
          { code: 'SAUDI-ARABIA-PRO-LEAGUE', name: 'Saudi Arabia Pro League' },
          { code: 'SAUDI-ARABIA-FIRST-DIVISION', name: 'Saudi Arabia First Division' },
          { code: 'SAUDI-ARABIA-KINGS-CUP', name: 'Saudi Arabia Kings Cup' },
          { code: 'SAUDI-ARABIA-PREMIER-LEAGUE', name: 'Saudi Arabia Premier League' },
        ],
      },
      uae: {
        name: 'United Arab Emirates',
        leagues: [
          { code: 'UAE-PRO-LEAGUE', name: 'UAE Pro League' },
        ],
      },
      qatar: {
        name: 'Qatar',
        leagues: [
          { code: 'QATAR-STARS-LEAGUE', name: 'Qatar Stars League' },
        ],
      },
      iran: {
        name: 'Iran',
        leagues: [
          { code: 'IRAN-PERSIAN-GULF-PRO-LEAGUE', name: 'Iran Persian Gulf Pro League' },
        ],
      },
      australia: {
        name: 'Australia',
        leagues: [
          { code: 'AUSTRALIA-A-LEAGUE-MEN', name: 'Australia A-League Men' },
          { code: 'AUSTRALIA-A-LEAGUE-WOMEN', name: 'Australia A-League Women' },
        ],
      },
      india: {
        name: 'India',
        leagues: [
          { code: 'INDIA-ISL', name: 'India Indian Super League' },
          { code: 'INDIA-I-LEAGUE', name: 'India I-League' },
        ],
      },
      thailand: {
        name: 'Thailand',
        leagues: [
          { code: 'THAILAND-THAI-LEAGUE-1', name: 'Thailand Thai League 1' },
        ],
      },
      indonesia: {
        name: 'Indonesia',
        leagues: [
          { code: 'INDONESIA-LIGA-1', name: 'Indonesia Liga 1' },
        ],
      },
      vietnam: {
        name: 'Vietnam',
        leagues: [
          { code: 'VIETNAM-LEAGUE-1', name: 'Vietnam League 1' },
        ],
      },
      malaysia: {
        name: 'Malaysia',
        leagues: [
          { code: 'MALAYSIA-SUPER-LEAGUE', name: 'Malaysia Super League' },
        ],
      },
      singapore: {
        name: 'Singapore',
        leagues: [
          { code: 'SINGAPORE-PREMIER-LEAGUE', name: 'Singapore Premier League' },
        ],
      },
      uzbekistan: {
        name: 'Uzbekistan',
        leagues: [
          { code: 'UZBEKISTAN-SUPER-LEAGUE', name: 'Uzbekistan Super League' },
        ],
      },
    },
    competitions: [
      { code: 'AFC-CHAMPIONS-LEAGUE', name: 'AFC Champions League Elite' },
      { code: 'AFC-CHAMPIONS-LEAGUE-2', name: 'AFC Champions League Two' },
      { code: 'AFC-CUP', name: 'AFC Cup' },
      { code: 'AFC-ASIAN-CUP', name: 'AFC Asian Cup' },
      { code: 'AFC-QUALIFIERS', name: 'AFC World Cup Qualifiers' },
    ],
  },
  africa: {
    name: 'Africa',
    countries: {
      egypt: {
        name: 'Egypt',
        leagues: [
          { code: 'EGYPT-EGYPTIAN-PREMIER-LEAGUE', name: 'Egypt Egyptian Premier League' },
        ],
      },
      morocco: {
        name: 'Morocco',
        leagues: [
          { code: 'MOROCCO-BOTOLA-PRO', name: 'Morocco Botola Pro' },
        ],
      },
      southAfrica: {
        name: 'South Africa',
        leagues: [
          { code: 'SOUTH-AFRICA-PSL', name: 'South Africa Premier Soccer League' },
        ],
      },
      nigeria: {
        name: 'Nigeria',
        leagues: [
          { code: 'NIGERIA-NPFL', name: 'Nigeria Professional Football League' },
        ],
      },
      algeria: {
        name: 'Algeria',
        leagues: [
          { code: 'ALGERIA-LIGUE-1', name: 'Algeria Ligue Professionnelle 1' },
        ],
      },
      tunisia: {
        name: 'Tunisia',
        leagues: [
          { code: 'TUNISIA-LIGUE-1', name: 'Tunisia Ligue Professionnelle 1' },
        ],
      },
      ghana: {
        name: 'Ghana',
        leagues: [
          { code: 'GHANA-PREMIER-LEAGUE', name: 'Ghana Premier League' },
        ],
      },
      senegal: {
        name: 'Senegal',
        leagues: [
          { code: 'SENEGAL-PREMIER-LEAGUE', name: 'Senegal Premier League' },
        ],
      },
      cameroon: {
        name: 'Cameroon',
        leagues: [
          { code: 'CAMEROON-ELITE-ONE', name: 'Cameroon Elite One' },
        ],
      },
      ivoryCoast: {
        name: 'Ivory Coast',
        leagues: [
          { code: 'IVORY-COAST-LIGUE-1', name: 'Ivory Coast Ligue 1' },
        ],
      },
      drcongo: {
        name: 'DR Congo',
        leagues: [
          { code: 'CONGO-BRAZZAVILLE-LINAFOOT', name: 'Congo Brazzaville Linafoot' },
        ],
      },
    },
    competitions: [
      { code: 'CAF-CHAMPIONS-LEAGUE', name: 'CAF Champions League' },
      { code: 'CAF-CONFEDERATION-CUP', name: 'CAF Confederation Cup' },
      { code: 'CAF-SUPER-CUP', name: 'CAF Super Cup' },
      { code: 'AFCON', name: 'Africa Cup of Nations' },
      { code: 'CAF-QUALIFIERS', name: 'CAF World Cup Qualifiers' },
    ],
  },
  oceania: {
    name: 'Oceania',
    countries: {
      newZealand: {
        name: 'New Zealand',
        leagues: [
          { code: 'NEW-ZEALAND-FC', name: 'New Zealand Football Championship' },
        ],
      },
    },
    competitions: [
      { code: 'OCEANIA-CHAMPIONS-LEAGUE', name: 'Oceania Champions League' },
      { code: 'OCEANIA-NATIONS-CUP', name: 'Oceania Nations Cup' },
    ],
  },
  international: {
    name: 'International',
    competitions: [
      { code: 'WC', name: 'World Cup' },
      { code: 'WC-QUALIFIERS', name: 'World Cup Qualifiers' },
      { code: 'FIFA-CLUB-WORLD-CUP', name: 'FIFA World Cup 2026' },
      { code: 'FIFA-WOMENS-WORLD-CUP', name: 'FIFA Women\'s World Cup' },
      { code: 'OLYMPICS-MENS', name: 'Olympic Games (Men)' },
      { code: 'OLYMPICS-WOMENS', name: 'Olympic Games (Women)' },
      { code: 'FIFA-U20-WORLD-CUP', name: 'FIFA U-20 World Cup' },
      { code: 'FIFA-U17-WORLD-CUP', name: 'FIFA U-17 World Cup' },
      { code: 'FRIENDLIES', name: 'Friendlies' },
      { code: 'WC-QUALIFIERS-EUROPE', name: 'World Cup Qualifiers Europe' },
      { code: 'WC-QUALIFIERS-ASIA', name: 'World Cup Qualifiers Asia' },
      { code: 'WC-QUALIFIERS-AFRICA', name: 'World Cup Qualifiers Africa' },
      { code: 'WC-QUALIFIERS-NORTH-AMERICA', name: 'World Cup Qualifiers North America' },
      { code: 'WC-QUALIFIERS-SOUTH-AMERICA', name: 'World Cup Qualifiers South America' },
      { code: 'WC-QUALIFIERS-OCEANIA', name: 'World Cup Qualifiers Oceania' },
      { code: 'WC-QUALIFIERS-INTERNATIONAL', name: 'World Cup Qualifiers International' },
    ],
  },
};